'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';

interface CustomCounterProps {
  from?: number;
  to: number;
  duration?: number;
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div';
  className?: string;
}

const CustomCounter: React.FC<CustomCounterProps> = ({
  from = 0,
  to,
  duration = 2,
  tag = 'h2',
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<
    HTMLHeadingElement | HTMLParagraphElement | HTMLDivElement | null
  >(null);
  const controls = useAnimation();

  const MotionTag = motion[tag] || motion.div;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          controls.start({ opacity: 1, y: 0 });
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [controls]);

  return (
    <MotionTag
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={controls}
      transition={{ duration: 0.5 }}
      className={className}
    >
      {isVisible ? <CountUp from={from} to={to} duration={duration} /> : from}
    </MotionTag>
  );
};

const CountUp: React.FC<CustomCounterProps> = ({
  from = 0,
  to,
  duration = 2,
}) => {
  const [count, setCount] = useState(from);

  useEffect(() => {
    let startTimestamp: number | null = null;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min(
        (timestamp - startTimestamp) / (duration * 1000),
        1
      );
      setCount(Math.floor((from ?? 0) + progress * (to - (from ?? 0))));

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setCount(to);
      }
    };

    requestAnimationFrame(step);

    return () => setCount(from);
  }, [from, to, duration]);

  return <>{count}</>;
};

export default CustomCounter;
